// apps/office/src/components/office/PaymentReminderPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import paymentBg from '../../pages/public/Payment.png';

/* ── Modern palette ─────────────────────────────────────── */
const C = {
  danger:    '#dc2626',
  dangerDark:'#991b1b',
  dangerBg:  '#fef2f2',
  success:   '#16a34a',
  successBg: '#f0fdf4',
  ink:       '#0f172a',
  body:      '#334155',
  muted:     '#64748b',
  border:    '#e2e8f0',
  card:      '#ffffff',
  cardBg:    '#f8fafc',
  accent:    '#4338ca',
};

const S = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: C.body,
    paddingBottom: 30,
  },
  bg: { position: 'absolute', top: 0, left: 0, width: 595.28, height: 841.89 },
  content: { paddingHorizontal: 45, paddingTop: 195 },

  /* ── Banner ── */
  banner: {
    backgroundColor: C.danger,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bannerTitle: { color: '#fff', fontSize: 12, fontWeight: 'bold', letterSpacing: 1 },
  bannerSub:   { color: '#fee2e2', fontSize: 7.5, letterSpacing: 0.4 },

  /* ── Meta ── */
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  metaLabel: { fontSize: 7.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 },
  metaValue: { fontSize: 9.5, color: C.ink, marginTop: 2 },
  metaBig:   { fontSize: 11, color: C.dangerDark, fontWeight: 'bold', marginTop: 2 },
  metaRight: { alignItems: 'flex-end' },

  /* ── Client card ── */
  clientBox: {
    backgroundColor: C.cardBg,
    padding: 10,
    borderRadius: 5,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: C.danger,
  },
  clientLbl:  { fontSize: 7.5, color: C.dangerDark, fontWeight: 'bold', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 },
  clientName: { fontSize: 11.5, fontWeight: 'bold', color: C.ink, marginBottom: 3 },
  clientLine: { fontSize: 9, color: C.body, marginBottom: 1.5, lineHeight: 1.35 },

  /* ── Account Summary (compact) ── */
  summary: {
    backgroundColor: C.cardBg,
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: C.border,
  },
  summaryTitle: {
    fontSize: 9,
    color: C.muted,
    fontWeight: 'bold',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    marginBottom: 8,
    paddingBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  summaryLbl: { fontSize: 10, color: C.muted },
  summaryVal: { fontSize: 11, color: C.ink, fontWeight: 'bold' },
  summaryValOk: { fontSize: 11, color: C.success, fontWeight: 'bold' },

  /* ── Amount Due bar (compact) ── */
  dueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: C.danger,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginTop: 8,
  },
  dueLbl: { color: '#ffffff', fontSize: 11, fontWeight: 'bold', letterSpacing: 0.6 },
  dueVal: { color: '#ffffff', fontSize: 13, fontWeight: 'bold' },

  /* ── ACCOUNT CLEAR notice (when due = 0) ── */
  clearBox: {
    backgroundColor: C.successBg,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 5,
    borderLeftWidth: 3,
    borderLeftColor: C.success,
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: C.success,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  clearSub: { fontSize: 8.5, color: C.body, lineHeight: 1.35 },

  /* ── Notes ── */
  notes: {
    marginTop: 8,
    padding: 10,
    backgroundColor: C.dangerBg,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: C.danger,
  },
  notesLbl: {
    fontSize: 8,
    color: C.dangerDark,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  notesTxt: { fontSize: 9, color: C.body, lineHeight: 1.5 },

  /* ── Footer ── */
  thanks:  { marginTop: 18, textAlign: 'center', fontSize: 11, color: C.dangerDark, fontWeight: 'bold' },
  subFoot: { marginTop: 5, textAlign: 'center', fontSize: 7.5, color: '#94a3b8' },
});

const rs = (v) => {
  const n = Number(v) || 0;
  return `Rs. ${new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const PaymentReminderPDF = ({ client, invoices = [], payments = [] }) => {
  const validInvoices = invoices.filter((i) => (i.field || 'Draft') !== 'Cancelled');

  const totalInvoiced = validInvoices.reduce(
    (s, i) => s + (Number(i.totalAmount) || Number(i.total) || 0),
    0
  );

  const totalPaid = payments
    .filter((p) => (p.status || 'Completed') === 'Completed')
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const totalDue = Math.max(0, totalInvoiced - totalPaid);
  const isClear = totalDue <= 0;

  return (
    <Document
      title={`Payment Reminder - ${client?.name || ''}`}
      author="HouseDesigns99"
      subject="Payment Reminder"
    >
      <Page size="A4" style={S.page}>
        <Image src={paymentBg} style={S.bg} fixed />

        <View style={S.content}>
          {/* Banner */}
          <View style={S.banner}>
            <Text style={S.bannerTitle}>PAYMENT REMINDER</Text>
            <Text style={S.bannerSub}>Action Required</Text>
          </View>

          {/* Meta */}
          <View style={S.metaRow}>
            <View>
              <Text style={S.metaLabel}>Statement Date</Text>
              <Text style={S.metaValue}>{fmtDate(new Date())}</Text>
            </View>
            <View style={S.metaRight}>
              <Text style={S.metaLabel}>Job ID</Text>
              <Text style={S.metaBig}>{client?.jobId || '—'}</Text>
            </View>
          </View>

          {/* Client */}
          <View style={S.clientBox}>
            <Text style={S.clientLbl}>Statement For</Text>
            <Text style={S.clientName}>{client?.name || '—'}</Text>
            {!!client?.phone && <Text style={S.clientLine}>Phone: {client.phone}</Text>}
            {!!client?.email && <Text style={S.clientLine}>Email: {client.email}</Text>}
            {!!client?.address && <Text style={S.clientLine}>Address: {client.address}</Text>}
          </View>

          {/* Account Summary — compact */}
          <View style={S.summary}>
            <Text style={S.summaryTitle}>Account Summary</Text>

            <View style={S.summaryRow}>
              <Text style={S.summaryLbl}>Total Invoiced Amount</Text>
              <Text style={S.summaryVal}>{rs(totalInvoiced)}</Text>
            </View>

            <View style={S.summaryRow}>
              <Text style={S.summaryLbl}>Total Received</Text>
              <Text style={S.summaryValOk}>− {rs(totalPaid)}</Text>
            </View>

            {isClear ? (
              /* ACCOUNT CLEAR notice instead of Due bar */
              <View style={S.clearBox}>
                <View style={{ flex: 1 }}>
                  <Text style={S.clearTitle}>✓ Account Clear</Text>
                  <Text style={S.clearSub}>
                    No outstanding balance — thank you for your prompt payments.
                  </Text>
                </View>
              </View>
            ) : (
              <View style={S.dueRow}>
                <Text style={S.dueLbl}>AMOUNT DUE</Text>
                <Text style={S.dueVal}>{rs(totalDue)}</Text>
              </View>
            )}
          </View>

          {/* Notes — only when there's an amount due */}
          {!isClear && (
            <View style={S.notes}>
              <Text style={S.notesLbl}>Please Note</Text>
              <Text style={S.notesTxt}>
                Our records show an outstanding balance of {rs(totalDue)} against your
                account. Kindly arrange the payment at your earliest convenience. If
                you have already made the payment, please share the transaction
                reference so we can update our records.
              </Text>
            </View>
          )}

          <Text style={S.thanks}>Thank you for your business!</Text>
          <Text style={S.subFoot}>
            This is a computer-generated statement and does not require a signature.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PaymentReminderPDF;
